import type { Landmark } from '../../../data/types';
import type { Ring, Vec2 } from '../../../geo/types';
import type { MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { beamSolid, boxSolid, horizontalCap } from '../solids';
import { hipRoof } from '../roofSolids';
import { archedBlock } from './fortress/fortressBlocks';
import { defensiveMasonryFace } from '../fortifications/towerMasonry';
import type { MasonryStyles } from './masonry';
import { meshFrameOf, planBounds, planFrameOf, toLayout } from './planFrame';

/** The obsolete medieval Olofspoort remained a prison in 1538 and was removed in 1618.
 * The 1544 woodcut identifies its square roofed gatehouse beside the chapel. Municipal/Rijksmuseum
 * later reconstructions corroborate the arch, upper prison windows and projecting parapet; they
 * are not contemporary elevation surveys. Source dimensions and hidden construction are estimates.
 */
export function buildSintOlofspoort(
  builder: MeshBuilder,
  landmark: Landmark,
  styles: { readonly masonry: MasonryStyles; readonly timber: VertexStyle }
): number {
  const before = builder.triangleCount;
  const plan = planFrameOf(landmark.footprint, landmark.heading);
  const frame = meshFrameOf(plan);
  const { u0, u1, v0, v1 } = planBounds(plan);
  const { masonry, timber } = styles;
  // Keep the projecting corbels, parapet and coping inside the complete source reservation.
  const inset = 0.3;
  const ring: Ring = [
    [u0 + inset, v0 + inset],
    [u1 - inset, v0 + inset],
    [u1 - inset, v1 - inset],
    [u0 + inset, v1 - inset],
  ];
  archedBlock(builder, masonry.wall, frame, {
    u: [ring[0]![0], ring[1]![0]],
    v: [ring[0]![1], ring[2]![1]],
    foot: -0.4,
    top: 5.0,
    passage: { v: [-1.8, 1.8], springing: 2.5 },
    openTop: true,
  });
  ring.forEach((a, i) => {
    const b = ring[(i + 1) % ring.length]!;
    const width = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const end = i % 2 === 1;
    const holes = (end ? [0.28, 0.72] : [0.32, 0.68]).map((share) => ({
      x: share * width - (end ? 0.5 : 0.22),
      y: 1.4,
      width: end ? 1.0 : 0.44,
      height: end ? 2.4 : 1.5,
      cuts: [],
    }));
    holes.push({ x: width / 2 - 0.3, y: 5.3, width: 0.6, height: 1.15, cuts: [] });
    defensiveMasonryFace(builder, masonry.wall, frame, a, b, 5, 7.8, holes, masonry.opening);
    // Bars sit behind the reveal, rather than in front of an uncut facade.
    const along: Vec2 = [(b[0] - a[0]) / width, (b[1] - a[1]) / width];
    for (const h of holes) {
      for (const share of [0.25, 0.5, 0.75]) {
        const x = h.x + h.width * share;
        const u = a[0] + along[0] * x - along[1] * 0.2;
        const v = a[1] + along[1] * x + along[0] * 0.2;
        beamSolid(builder, timber, frame, [u, 5 + h.y, -v], [u, 5 + h.y + h.height, -v], 0.045);
      }
    }
  });
  horizontalCap(builder, masonry.wall, frame, ring, 12.8, true);

  // Projecting stone brackets physically carry the parapet ring above the prison rooms.
  for (const u of [u0 + 1.0, (u0 + u1) / 2, u1 - 1.0]) {
    for (const v of [v0, v1 - 0.35])
      boxSolid(builder, masonry.wall, frame, [u - 0.23, 12.05, v], [u + 0.23, 12.95, v + 0.35]);
  }
  for (const v of [v0 + 1.0, (v0 + v1) / 2, v1 - 1.0]) {
    for (const u of [u0, u1 - 0.35])
      boxSolid(builder, masonry.wall, frame, [u, 12.05, v - 0.23], [u + 0.35, 12.95, v + 0.23]);
  }
  const rim = 0.38;
  for (const v of [v0 + 0.02, v1 - rim - 0.02]) {
    boxSolid(builder, masonry.wall, frame, [u0 + 0.02, 12.75, v], [u1 - 0.02, 13.65, v + rim]);
    for (let u = u0 + 0.12; u + 0.65 < u1 - 0.05; u += 1.45)
      boxSolid(builder, masonry.wall, frame, [u, 13.62, v], [u + 0.65, 14.2, v + rim]);
  }
  for (const u of [u0 + 0.02, u1 - rim - 0.02]) {
    boxSolid(
      builder,
      masonry.wall,
      frame,
      [u, 12.75, v0 + rim + 0.04],
      [u + rim, 13.65, v1 - rim - 0.04]
    );
    for (let v = v0 + rim + 0.08; v + 0.65 < v1 - rim - 0.04; v += 1.45)
      boxSolid(builder, masonry.wall, frame, [u, 13.62, v], [u + rim, 14.2, v + 0.65]);
  }
  // A squat hip sits behind the battlement rather than converting the prison into a later spire.
  const roofRing: Ring = [
    [u0 + 0.65, v0 + 0.65],
    [u1 - 0.65, v0 + 0.65],
    [u1 - 0.65, v1 - 0.65],
    [u0 + 0.65, v1 - 0.65],
  ];
  hipRoof(builder, masonry.roof, frame, {
    u0: roofRing[0]![0],
    u1: roofRing[1]![0],
    v0: roofRing[0]![1],
    v1: roofRing[2]![1],
    eaveY: 13.0,
    pitch: (42 * Math.PI) / 180,
  });
  horizontalCap(builder, masonry.wall, frame, roofRing, 13, false);
  return builder.triangleCount - before;
}

/** Only the gate piers obstruct the ground. The existing Olofspoort alley passes through its arch. */
export function sintOlofspoortObstacles(landmark: Landmark): readonly Ring[] {
  const plan = planFrameOf(landmark.footprint, landmark.heading);
  const { u0, u1, v0, v1 } = planBounds(plan);
  const pier = (a: number, b: number): Ring =>
    (
      [
        [u0 + 0.3, a],
        [u1 - 0.3, a],
        [u1 - 0.3, b],
        [u0 + 0.3, b],
      ] as Ring
    ).map((p) => toLayout(plan, p));
  return [pier(v0 + 0.3, -1.8), pier(1.8, v1 - 0.3)];
}
